'use client'

import { ChevronRightIcon } from '@heroicons/react/24/outline'
import { XMarkIcon } from '@heroicons/react/24/solid'
import Image from 'next/image'
import { usePathname } from 'next/navigation'

import { Button } from '@src/components/Button/Button'
import { LocalizedLink } from '@src/components/LocalizedLink/LocalizedLink'
import { useTopNav } from '@src/components/TopNavV4/TopNavContext'
import { useSession } from '@src/hooks/useSession'
import { useTrackSelectNavCta } from '@src/utils/analytics.util'
import { cn } from '@src/utils/tailwind'

export const TopNavAccountPanelContent = () => {
  const { setAccountExpanded, account } = useTopNav()
  const { user } = useSession()
  const trackSelectNavCta = useTrackSelectNavCta()
  const pathname = usePathname()

  const logOutHref = `/auth/akamai/logout?redirectUrl=${pathname}`
  const logInHref = `/auth/akamai/login?redirectUrl=${pathname}`

  const welcomeText =
    user?.firstName && user?.firstName?.toLowerCase() !== 'guest'
      ? `${account?.welcomeCopy}, ${user?.firstName} ${user?.lastName}.`
      : `${account?.welcomeCopy}!`

  const isLoggedIn = Boolean(user?.email)

  const renderLinks = () => {
    return (
      <div className="flex flex-col items-start">
        {account?.links?.map((link, i) => (
          <LocalizedLink
            key={i}
            href={link?.href ?? ''}
            rel={link?.newTab ? 'noopener noreferrer' : ''}
            target={link?.newTab ? '_blank' : '_self'}
            className="text-heading-4 py-3 hover:underline"
            onClick={() => trackSelectNavCta({ 'cta url': link?.href })}
          >
            {link?.title}
          </LocalizedLink>
        ))}
      </div>
    )
  }

  const renderLoggedInContent = () => {
    return (
      <div className="flex flex-col">
        <div className="mb-8 text-body-base text-bodi-gray-butters">{welcomeText}</div>
        <div className="mb-5 flex flex-col gap-2">
          {account?.loggedInLinks?.map((link, i) => (
            <LocalizedLink
              key={i}
              href={link?.href ?? ''}
              rel={link?.newTab ? 'noopener noreferrer' : ''}
              target={link?.newTab ? '_blank' : '_self'}
              className="flex items-center justify-between gap-2 rounded-[30px] border border-bodi-gray-dark p-4 transition-colors hover:bg-bodi-gray-dark"
              onClick={() => trackSelectNavCta({ 'cta url': link?.href })}
            >
              <div className="flex items-center gap-2">
                <Image
                  className="shrink-0 rounded-xl bg-bodi-gray-dark object-cover"
                  width={48}
                  height={48}
                  src={link?.imageUrl ?? ''}
                  alt={link?.imageAlt ?? ''}
                />

                <div className="flex flex-col">
                  <div className="text-heading-5">{link?.title}</div>
                  <div className="text-body-sm text-bodi-gray-butters">{link?.subtitle}</div>
                </div>
              </div>

              <ChevronRightIcon className="size-6 shrink-0 stroke-[2px]" />
            </LocalizedLink>
          ))}
        </div>

        {renderLinks()}

        <a
          href={logOutHref}
          className="mt-8 self-center font-medium text-bodi-green-bright-500 underline"
        >
          {account?.signOutCopy}
        </a>
      </div>
    )
  }

  const renderLoggedOutContent = () => {
    return (
      <div className="mt-8 flex flex-col gap-5">
        <a
          onClick={() => {
            trackSelectNavCta({ 'cta url': logInHref })
          }}
          data-testid="logInBtn"
          href={logInHref}
          className="flex cursor-pointer gap-2 hover:text-bodi-white"
        >
          <Button size="medium" className="w-full">
            {account?.logInCopy}
          </Button>
        </a>
        {renderLinks()}
      </div>
    )
  }

  return (
    <div className="h-full bg-bodi-black-text bg-gradient-to-t from-white/10 to-white/0 p-4 text-white lg:p-8">
      <div
        className={cn(
          'mb-1 flex flex-col items-start justify-between gap-4',
          'lg:flex-row lg:items-center lg:gap-0'
        )}
      >
        {account?.title && (
          <div className="text-heading-display-3 order-last lg:order-first">{account?.title}</div>
        )}
        <button
          className="self-end rounded-md p-1 hover:bg-bodi-gray-dark lg:self-auto"
          onClick={() => setAccountExpanded(false)}
        >
          <XMarkIcon className="size-6 stroke-[2px]" />
        </button>
      </div>
      {isLoggedIn ? renderLoggedInContent() : renderLoggedOutContent()}
    </div>
  )
}
